/**
 * Text 3D Flip — el efecto del boton "Agregar al carrito".
 *
 * PORT del componente de Magic UI (https://magicui.design/docs/components/text-3d-flip).
 * NO es una copia pegada: el original se instala con shadcn y depende de
 * `motion/react`, de Tailwind y de `cn()`. Este proyecto no usa ninguno de
 * los tres (el CSS es propio, con los tokens de marca) y meterlos para un
 * solo efecto seria pisar el sistema de estilos que ya existe.
 *
 * Asi que esta es una reimplementacion con el MISMO comportamiento, sacada
 * del codigo real del registry de Magic UI:
 *
 *   - el texto se parte en caracteres (Intl.Segmenter, con fallback a Array.from);
 *   - cada caracter va en una caja con DOS caras (frontal y trasera) con
 *     `backface-visibility: hidden`, igual que el original;
 *   - al hacer hover la caja rota 90 grados en el eje elegido;
 *   - el giro arranca escalonado, caracter por caracter;
 *   - el texto completo queda aparte, para lectores de pantalla.
 *
 * Las tablas de transformadas son las del original, copiadas de
 * https://magicui.design/r/text-3d-flip.json:
 *
 *   ROTATION_MAP          top: rotateX(90deg)   right: rotateY(90deg)
 *                          bottom: rotateX(-90deg) left: rotateY(-90deg)
 *   CONTAINER_TRANSFORMS  top/bottom: translateZ(-0.5lh)
 *                          left/right: rotateY(90deg) translateX(50%) rotateY(-90deg)
 *   FRONT_FACE_TRANSFORMS top/bottom: translateZ(0.5lh)
 *                          left: rotateY(90deg) translateX(50%) rotateY(-90deg)
 *                          right: rotateY(-90deg) translateX(50%) rotateY(90deg)
 *   SECOND_FACE_TRANSFORMS top: rotateX(-90deg) translateZ(0.5lh)
 *                          bottom: rotateX(90deg) translateZ(0.5lh)
 *                          right/left: rotateY(90deg) translateX(50%) rotateY(-90deg) ...
 *
 * Lo que cambia contra el original: el giro lo hace CSS (una animacion con
 * `transform` y un `animation-delay` por caracter) en vez de `useAnimate` de
 * Motion. Se ve igual y no suma ningun bundle.
 *
 * El hover lo dispara el CSS desde el boton (`.btn--flip:hover`), asi que
 * tambien se ve al navegar con el teclado (`:focus-visible`). En mobile no hay
 * hover: el boton agrega la clase `.btn--flip--activo` cuando el producto entra
 * al carrito, y esa clase dispara la misma animacion.
 */
'use client';

import { memo, type CSSProperties, type ElementType } from 'react';

import './text-3d-flip.css';

/** Las cuatro direcciones de giro, con su transformada. */
const ROTACION = {
  top: 'rotateX(90deg)',
  right: 'rotateY(90deg)',
  bottom: 'rotateX(-90deg)',
  left: 'rotateY(-90deg)',
} as const;

/**
 * La transformada del contenedor segun la direccion. Compensa la
 * translateZ de la cara frontal para que, en reposo, se lea derecho.
 */
const TRANSFORM_CONTENEDOR = {
  top: 'translateZ(-0.5lh)',
  bottom: 'translateZ(-0.5lh)',
  left: 'rotateY(90deg) translateX(50%) rotateY(-90deg)',
  right: 'rotateY(90deg) translateX(50%) rotateY(-90deg)',
} as const;

/** La cara frontal: la que se ve antes del giro. */
const TRANSFORM_FRONTAL = {
  top: 'translateZ(0.5lh)',
  bottom: 'translateZ(0.5lh)',
  left: 'rotateY(90deg) translateX(50%) rotateY(-90deg)',
  right: 'rotateY(-90deg) translateX(50%) rotateY(90deg)',
} as const;

/** La cara trasera: la que queda mirando al lector cuando la caja gira. */
const TRANSFORM_POSTERIOR = {
  top: 'rotateX(-90deg) translateZ(0.5lh)',
  bottom: 'rotateX(90deg) translateZ(0.5lh)',
  left: 'rotateY(90deg) translateX(50%) rotateY(-90deg)',
  right: 'rotateY(-90deg) translateX(50%) rotateY(90deg)',
} as const;

export type DireccionGiro = keyof typeof ROTACION;

export interface Props {
  /** El texto a animar. */
  children: string;
  /** Elemento que envuelve todo. Por defecto un `span`. */
  as?: ElementType;
  /** Clase del contenedor. */
  className?: string;
  /** Clase de cada caracter. */
  charClassName?: string;
  /** Clase de la cara frontal. */
  textClassName?: string;
  /** Clase de la cara trasera. */
  flipTextClassName?: string;
  /** Segundos de retraso entre un caracter y el siguiente. */
  staggerDuration?: number;
  /**
   * Desde donde arranca el escalonado. No se incluye `random` (como el
   * original) porque acá el retraso se calcula al renderizar y tiene que
   * ser el mismo en el servidor y en el cliente.
   */
  staggerFrom?: 'first' | 'last' | 'center' | number;
  /** Eje del giro. */
  rotateDirection?: DireccionGiro;
}

/** Parte el texto en caracteres, sin partir los emojis ni los acentos. */
function partirEnCaracteres(texto: string): string[] {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const segmenter = new Intl.Segmenter('es', { granularity: 'grapheme' });
    return Array.from(segmenter.segment(texto), ({ segment }) => segment);
  }
  return Array.from(texto);
}

/** El retraso de cada caracter, en segundos. */
function retrasoDe(
  indice: number,
  total: number,
  staggerFrom: Props['staggerFrom'],
  staggerDuration: number,
): number {
  if (staggerFrom === 'last') return (total - 1 - indice) * staggerDuration;
  if (staggerFrom === 'center') {
    const centro = Math.floor(total / 2);
    return Math.abs(centro - indice) * staggerDuration;
  }
  if (typeof staggerFrom === 'number') return Math.abs(staggerFrom - indice) * staggerDuration;
  return indice * staggerDuration;
}

const CajaCaracter = memo(function CajaCaracter({
  caracter,
  direccion,
  retraso,
  charClassName,
  textClassName,
  flipTextClassName,
}: {
  caracter: string;
  direccion: DireccionGiro;
  retraso: number;
  charClassName?: string;
  textClassName?: string;
  flipTextClassName?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={['text3d__char', charClassName].filter(Boolean).join(' ')}
      style={
        {
          '--text3d-contenedor': TRANSFORM_CONTENEDOR[direccion],
          '--text3d-frontal': TRANSFORM_FRONTAL[direccion],
          '--text3d-posterior': TRANSFORM_POSTERIOR[direccion],
          '--text3d-rotacion': ROTACION[direccion],
          '--text3d-retraso': `${retraso}s`,
        } as CSSProperties
      }
    >
      <span
        className={['text3d__cara', 'text3d__cara--frontal', textClassName].filter(Boolean).join(' ')}
      >
        {caracter}
      </span>
      <span
        className={['text3d__cara', 'text3d__cara--posterior', flipTextClassName].filter(Boolean).join(' ')}
      >
        {caracter}
      </span>
    </span>
  );
});

export function Text3DFlip({
  children,
  as: ElementTag = 'span',
  className,
  charClassName,
  textClassName,
  flipTextClassName,
  staggerDuration = 0.05,
  staggerFrom = 'first',
  rotateDirection = 'right',
}: Props) {
  const texto = typeof children === 'string' ? children : String(children ?? '');

  const palabras = texto.split(' ').map((palabra, i, todas) => ({
    caracteres: partirEnCaracteres(palabra),
    conEspacio: i !== todas.length - 1,
  }));

  const total = palabras.reduce((suma, palabra) => suma + palabra.caracteres.length, 0);

  // El indice global de cada caracter, para calcular el retraso escalonado.
  let acumulado = 0;

  return (
    <ElementTag className={['text3d', className].filter(Boolean).join(' ')}>
      {/* El texto completo, para lectores de pantalla y para el nombre accesible del boton. */}
      <span className="visually-hidden">{texto}</span>

      {palabras.map((palabra, indicePalabra) => {
        const desde = acumulado;
        acumulado += palabra.caracteres.length;

        return (
          <span className="text3d__palabra" key={indicePalabra}>
            {palabra.caracteres.map((caracter, indiceCaracter) => (
              <CajaCaracter
                key={desde + indiceCaracter}
                caracter={caracter}
                direccion={rotateDirection}
                retraso={retrasoDe(desde + indiceCaracter, total, staggerFrom, staggerDuration)}
                charClassName={charClassName}
                textClassName={textClassName}
                flipTextClassName={flipTextClassName}
              />
            ))}
            {palabra.conEspacio ? <span className="text3d__espacio"> </span> : null}
          </span>
        );
      })}
    </ElementTag>
  );
}

export default Text3DFlip;
